<template>
  <section
    v-if="props.data"
    class="stepsSlider"
    :class="[ props.data.parentClasses, props.data.parentStyle ? props.data.parentStyle : 'defaultStyle' ]"
  >
    <div class="container row">
      <div class="col s12">
        <div class="option-text mb-4">
          <strong
            v-if="props.data.subTitle"
            class="label primary-color-text text-base-1 center-align text-primary"
          >
            {{ props.data.subTitle }}</strong>
          <h2
            v-if="props.data.title"
            class="center-align text-second-800 text-[22px] md:text-[24px] mb-2"
            v-html="convertToHtml(props.data.title)"
          ></h2>
        </div>
      </div>
      <div class="col s12 sliderHeading">
        <ClientOnly>
          <Swiper
            :slides-per-view="4"
            :loop="true"
            :space-between="16"
            :breakpoints="{
              0: {
                slidesPerView: 2,
              },
              480: {
                slidesPerView: 2,
              },
              650: {
                slidesPerView: 2
              },
              993: {
                slidesPerView: 4
              }
            }"
            @swiper="onSwiperHeading"
          >
            <SwiperSlide
              v-for="item, i in props.data.items"
              :key="item"
              class="sliderHeading__item"
              :class="{active: currentItem ? currentItem === i*1 : i*1 === 0}"
              @click="currentItem = i*1"
            >
              <span class="slide-title"> {{ item.title }}</span>
            </SwiperSlide>
          </Swiper>
        </ClientOnly>
      </div>

      <div class="col s12 sliderContent">
        <ClientOnly>
          <Swiper
            :modules="[Autoplay]"
            :slides-per-view="1"
            :loop="false"
            :auto-height="true"
            :autoplay="{
              delay: 60000
            }"
            @swiper="onSwiper"
            @slide-change="onSlideChange"
          >
            <SwiperSlide
              v-for="item in props.data.items"
              :key="item"
              class="sliderContent__item"
            >
              <div class="row">
                <div class="col s12 m12 l7 sliderContent__item__image">
                  <nuxt-picture
                    preload
                    :img-attrs="{class: 'responsive-img', alt: ''}"
                    :src="item.image"
                    width="650"
                    height="530"
                    :placeholder="[100, 50, 10]"
                    sizes=""
                    loading="lazy"
                  />
                </div>
                <div class="col s12 m12 l5">
                  <div class="heading">
                    <h3 class="title">
                      {{ item.title }}
                    </h3>
                    <div v-html="convertToHtml(item.descr)"></div>
                  </div>

                  <ul
                    v-if="item.list.length"
                    class="subItems"
                  >
                    <li
                      v-for="item2 in item.list"
                      :key="item2"
                      class="row"
                    >
                      <div
                        v-if="item2.image"
                        class="col s3 img-box"
                      >
                        <nuxt-picture
                          preload
                          :img-attrs="{class: '', alt: item2.title}"
                          :src="item2.image"
                          width="106"
                          height="106"
                          :placeholder="[100, 50, 10]"
                          sizes=""
                          loading="lazy"
                        />
                      </div>
                      <div
                        class="col s9 title-box"
                        :class="item2.image ? 's9' : 's12'"
                      >
                        <h4>{{ item2.title }}</h4>
                        <div
                          class="small"
                          v-html="convertToHtml(item2.descr)"
                        ></div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </ClientOnly>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'

const stepsSliderHeading = ref(null)
const stepsSlider = ref(null)
const currentItem = ref(null)

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  }
})

/**
* Swiper actions
*/
const onSwiperHeading = (swiper) => {
  stepsSliderHeading.value = swiper
}

const onSwiper = (swiper) => {
  stepsSlider.value = swiper
}

const onSlideChange = (index) => {
  currentItem.value = index.activeIndex
}

/**
* If update current slide
*/
watch(currentItem, (newCurrentItem) => {
  if (currentItem !== newCurrentItem) {
    stepsSliderHeading.value.slideTo(newCurrentItem)
    stepsSlider.value.slideTo(newCurrentItem)
  }
})
</script>

<style lang="scss">
.stepsSlider {
  .option-text {
    h2 {
      @media only screen and (min-width: 993px) {
        font-size: 2.75rem;
      }
    }
  }
  .sliderHeading {
    margin-bottom: 3rem;
    counter-reset: count;
    &__item {
      position: relative !important;
      padding-bottom: 2rem;
      counter-increment: count;
      cursor: pointer;
      &:before {
        content: "";
        position: absolute;
        bottom: 1rem;
        left: 50%;
        z-index: 2;
        width: calc(100% - 2rem);
        height: 4px;
        background-color: #eaedf0;
        border-radius: 3px;
        transform: translateX(-50%);
      }
      &.active {
        &:after {
          content: "";
          position: absolute;
          bottom: 1rem;
          left: 1rem;
          z-index: 3;
          width: 0;
          height: 4px;
          background-color: color("primary-color", "base");
          border-radius: 3px;
          transform-origin: left center;
          animation: slideTimer 60s linear;
        }
        @keyframes slideTimer {
          from {
            width: 0;
          }
          to {
            width: calc(100% - 2rem);
          }
        }
      }
      &.active,
      &:hover {
        .slide-title {
          color: color("primary-color", "base-2");
        }
      }
      .slide-title {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        color: rgba(98,121,147,.7);
        font-size: 1.25rem;
        font-weight: 700;
        transition: all .3s ease;
        &:before {
          content: counter(count, decimal-leading-zero);
          display: inline-block;
          margin-right: 10px;
          color: color("primary-color", "base-2");
          font-weight: 700;
          transition: all .3s ease;
        }
      }
    }
  }

  .sliderContent {
    counter-reset: main-count;
    &__item {
      counter-increment: main-count;
      & > .row {
        align-items: center;
      }
      &__image {
        display: flex;
        justify-content: center;
        @media only screen and (max-width: 992px) {
          padding-bottom: 2rem;
        }
      }
      .heading {
        .title {
          display: inline-flex;
          align-items: flex-end;
          margin-bottom: 1.5rem;
          font-size: 2rem;
          &:after {
            content: counter(main-count, decimal-leading-zero);
            color: #28323d;
            font-size: 3.75rem;
            font-weight: 700;
            line-height: .9;
            letter-spacing: -1.4px;
            transform: translateX(-20px);
            opacity: .2;
          }
        }
      }
      .subItems {
        li {
          display: flex;
          align-items: center;
          padding: 2rem 0;
          border-bottom: 1px solid #eaedf0;
          @media only screen and (max-width: 992px) {
            padding: 1rem 0;
          }
          &:last-child {
            padding-bottom: 0;
            margin-bottom: 0;
            border-bottom: none;
          }
        }
      }
    }
  }
}
</style>
